/**
 * How a SERVICE arrives at its price — FLAT or DYNAMIC — and what follows.
 *
 * FLAT means the operator typed the number. DYNAMIC means it is derived:
 *
 *   (Billable Hours x Labor Rate) + (Material Cost x Markup)
 *     + (Equipment Cost x Markup) + Surcharges
 *
 * There is no third "time & materials" mode: hours x rate is the labor term of
 * that formula, not a peer of a flat price. See SPEC-line-item-model.
 *
 * Everything here is about PRICE. Hours are recorded on a flat service too and
 * cost real money there — that is `document-costing`, which never asks any of
 * these questions.
 */

export function isTMOverrideEligibleType(type: string | null | undefined): boolean {
  return (
    String(type ?? '')
      .trim()
      .toUpperCase() === 'SERVICE'
  );
}

/**
 * The line, as every question below asks about it.
 *
 * An OBJECT rather than positional arguments, because `isCustom` is the one a
 * caller can silently omit — and omitting it puts a hand-typed line back under
 * the T&M rewrite, which prices it from a labor rate it does not have. Named at
 * every call site, a missing answer is visible in the diff.
 */
export interface PricedLine {
  type?: string | null;
  priceStrategy?: string | null;
  isCustom?: boolean;
}

export function resolveEffectivePriceStrategy(
  line: PricedLine,
  pricingMode: 'STANDARD' | 'TM_OVERRIDE',
): string {
  // TM_OVERRIDE prices the DOCUMENT's services by time — but it can only do
  // that to a line the pricebook priced. A hand-typed line has no labor rate,
  // no markup profile and no dynamic configuration, so the rewrite resolved it
  // from `laborRate x hours` with a rate nothing had set. It states its price.
  return pricingMode === 'TM_OVERRIDE' && allowsDynamicPricing(line)
    ? 'DYNAMIC'
    : String(line.priceStrategy ?? 'FLAT');
}

/**
 * Do this line's HOURS drive its PRICE?
 *
 * True only for a dynamically priced service. A flat service records hours too,
 * and they buy it nothing — they are cost, exactly as a material's cost is. The
 * question this answers is narrowly "is the price computed from time", which is
 * why it is not named after having hours.
 */
export function pricesFromHours(
  line: PricedLine,
  pricingMode: 'STANDARD' | 'TM_OVERRIDE',
): boolean {
  return (
    isTMOverrideEligibleType(line.type) &&
    resolveEffectivePriceStrategy(line, pricingMode) === 'DYNAMIC'
  );
}

/** What the operator is shown about how this line is being priced. */
export function getDynamicPricingLabel(
  line: PricedLine,
  pricingMode: 'STANDARD' | 'TM_OVERRIDE',
): 'Dynamic' | 'Dynamic override' | null {
  if (!pricesFromHours(line, pricingMode)) return null;

  const rawStrategy = String(line.priceStrategy ?? 'FLAT')
    .trim()
    .toUpperCase();
  if (pricingMode === 'TM_OVERRIDE' && allowsDynamicPricing(line) && rawStrategy !== 'DYNAMIC') {
    // The document is pricing dynamically a service the catalog prices flat.
    return 'Dynamic override';
  }

  return 'Dynamic';
}

/**
 * Why a dynamically priced line cannot yet produce a price.
 *
 * Asked only of a line whose price is still waiting to be derived from
 * `laborRate x hours`. A materialized dynamic service is already priced — its
 * rates live in `laborLines` and it carries no single `laborRate` — so reading
 * that empty field as a missing pricebook rate warns about the one case that is
 * complete. Same reasoning, same gate, as `calcLineSubtotal` below.
 */
export function getDynamicPricingIncompleteReason(args: {
  type: string | null | undefined;
  priceStrategy: string | null | undefined;
  pricingMode: 'STANDARD' | 'TM_OVERRIDE';
  isCustom?: boolean;
  laborRate?: number | null;
  estimatedHours?: number | null;
  dynamicPricingConfig?: { enabled?: boolean } | null;
}): string | null {
  const { type, priceStrategy, pricingMode, laborRate, estimatedHours } = args;
  if (!pricesFromHours({ type, priceStrategy, isCustom: args.isCustom }, pricingMode)) {
    return null;
  }
  if (isMaterializedDynamicService({ type, dynamicPricingConfig: args.dynamicPricingConfig })) {
    return null;
  }

  if ((laborRate ?? 0) <= 0) {
    return 'Missing labor rate in pricebook';
  }

  if ((estimatedHours ?? 0) <= 0) {
    return 'Set hours to price this service';
  }

  return null;
}

/** What a T&M-priced SERVICE is missing before it can produce a price. */
export type TMPricingGap =
  /** Nothing is materialized and no hours are stated, so `hours x rate` is $0. */
  | 'HOURS'
  /** Materialized, and the configuration it was materialized from prices at $0. */
  | 'PRICE';

/** A line, as the question below asks about it. */
export interface TMPricedBasisLine {
  type?: string | null;
  priceStrategy?: string | null;
  isCustom?: boolean;
  estimatedHours?: number | null;
  basePrice?: number | null;
  dynamicPricingConfig?: { enabled?: boolean } | null;
}

/**
 * Why this line cannot produce a price under `pricingMode`, or null if it can.
 *
 * The ONE answer behind every "you can't save this yet" a T&M document gives —
 * the estimate submit gate and the estimate template's own validation both read
 * it, so the form and the template that produces it cannot disagree about which
 * lines are complete.
 *
 * It used to be spelled `estimatedHours > 0` in both places, and that stopped
 * being the invariant: a service the T&M switch MATERIALIZES keeps its time in
 * `dynamicPricingConfig.laborLines` and states no hours of its own, so a
 * materials-only service — real parts, a real price, zero hours — was refused by
 * the very rule meant to stop it quoting nothing. `assertTMHoursPresent` in
 * operations (`shared/document-pricing-rules.util.ts`) already exempts such a
 * line; these two were the readers still asking the old question.
 *
 * What the rule protects is unchanged, and is the whole reason it exists: a
 * service that cannot produce a price is still refused. Only the way it is
 * measured moves, to the number the document actually carries —
 *
 *   NOT materialized  its price is still waiting to be derived from
 *                     `hours x rate`, so hours are what it is missing.
 *   materialized      `basePrice` IS its price, already calculated from its
 *                     labor rows and its parts (SPEC-line-item-model, "A
 *                     materialized dynamic service is FLAT on the document").
 *                     A positive one means labor, parts, or both are behind it;
 *                     $0 means nothing is.
 *
 * The $0 half is deliberately CLIENT-ONLY, exactly as `LINE_ITEMS_REQUIRED` is:
 * operations accepts a $0 line, and a $0 quote is still never what the operator
 * meant.
 */
export function tmPricingGap(
  line: TMPricedBasisLine,
  pricingMode: 'STANDARD' | 'TM_OVERRIDE',
): TMPricingGap | null {
  // Not priced from time at all — a flat service, a part, a hand-typed line.
  // Its price is the number it states and this question does not apply.
  if (!pricesFromHours(line, pricingMode)) return null;

  if (isMaterializedDynamicService(line)) {
    return (line.basePrice ?? 0) > 0 ? null : 'PRICE';
  }

  return (line.estimatedHours ?? 0) > 0 ? null : 'HOURS';
}

/**
 * True when this line is a SERVICE the dynamic pricebook priced and froze onto
 * the document.
 *
 * Such a line's `basePrice` is the whole answer: labor lines, tiered rates,
 * markup profiles, complexity, after-hours, fee credits and the add-on discount
 * were all resolved at materialization. Re-deriving it from `laborRate x hours`
 * is not a rounding difference — a dynamic service keeps its time in
 * `laborLines` and carries no single hourly rate, so the derivation resolves to
 * $0 and the whole price disappears.
 *
 * Lockstep with `isMaterializedDynamicService` in the operations pricing engine
 * (`pricing-engine.interfaces.ts`), which is the code that actually bills. If
 * these two disagree the operator is shown one price and the customer charged
 * another.
 */
export function isMaterializedDynamicService(line: {
  type?: string | null;
  dynamicPricingConfig?: { enabled?: boolean } | null;
}): boolean {
  const config = line.dynamicPricingConfig;
  if (!config || typeof config !== 'object') return false;
  if (config.enabled !== true) return false;
  return isTMOverrideEligibleType(line.type);
}

export interface LineItemForCalc {
  type?: string | null;
  priceStrategy?: string | null;
  /** Hand-typed. Keeps the line out of the T&M rewrite — it has no rate. */
  isCustom?: boolean;
  quantity: number;
  basePrice: number;
  laborRate?: number | null;
  costBasis?: number | null;
  markupPercent?: number | null;
  estimatedHours?: number | null;
  dynamicPricingConfig?: { enabled?: boolean } | null;
}

export function calcLineSubtotal(
  item: LineItemForCalc,
  pricingMode: 'STANDARD' | 'TM_OVERRIDE' = 'STANDARD',
): number {
  const effectiveStrategy = resolveEffectivePriceStrategy(item, pricingMode);

  if (effectiveStrategy === 'DYNAMIC') {
    // A dynamic service was already priced when it was put on the document.
    if (isMaterializedDynamicService(item)) return item.basePrice * item.quantity;
    return resolveTMLineSubtotal({
      quantity: item.quantity,
      basePrice: item.basePrice,
      laborRate: item.laborRate,
      costBasis: item.costBasis,
      markupPercent: item.markupPercent,
      estimatedHours: item.estimatedHours ?? undefined,
    });
  }

  const effectivePrice =
    effectiveStrategy === 'LABOR_ONLY'
      ? (item.laborRate ?? 0)
      : effectiveStrategy === 'COST_PLUS'
        ? (item.costBasis ?? 0) * (1 + (item.markupPercent ?? 0) / 100)
        : item.basePrice;

  return effectivePrice * item.quantity;
}

/**
 * The dynamic formula for a WHOLE LINE — every term per unit, multiplied by how
 * many units are being sold.
 *
 * HOURS ARE PER UNIT. `estimatedHours` is the time ONE of the service takes,
 * which is how the pricebook stores it and how the labor editor writes it back.
 * Two of a two-hour service is four hours of work, costs twice as much to
 * deliver and sells for twice as much — so the labor term is multiplied by the
 * quantity exactly as the material term always has been.
 *
 * Reading it as a whole-line figure was the reported bug (NOT-630): the labor
 * term ignored the quantity, so a $300 service quoted as 2 hrs x $150/hr stayed
 * frozen at $300 however many the operator sold. The material term scaled, so
 * the operator watched HALF the line respond to the field.
 *
 * Lockstep with `resolveTMComponents` in the operations pricing engine, which
 * is the code that actually bills.
 */
export function resolveTMLineSubtotal(args: {
  quantity: number;
  basePrice: number;
  laborRate: number | null | undefined;
  costBasis: number | null | undefined;
  markupPercent: number | null | undefined;
  estimatedHours?: number;
}): number {
  const { quantity, laborRate, costBasis, markupPercent, estimatedHours } = args;
  const safeLaborRate = laborRate ?? 0;
  const safeCostBasis = costBasis ?? 0;
  const safeMarkupPercent = markupPercent ?? 0;
  const laborUnit = Math.max((estimatedHours ?? 0) * safeLaborRate, 0);
  const materialUnit = Math.max(safeCostBasis * (1 + safeMarkupPercent / 100), 0);
  return Math.max(quantity, 0) * (laborUnit + materialUnit);
}

/**
 * May this line be priced DYNAMICALLY, or only flat?
 *
 * Dynamic pricing derives the number from a labor rate, a markup profile and
 * the pricebook's dynamic configuration — every one of which belongs to a
 * CATALOG item. A line typed in by hand has no catalog row behind it to carry
 * any of them, so "dynamic" on such a line meant `laborRate x hours` against a
 * rate nothing had ever set: an operator who believed they had priced the line
 * had priced it at $0. Only the pricebook can price dynamically; a hand-typed
 * line states its price.
 */
export function allowsDynamicPricing(line: { type?: string | null; isCustom?: boolean }): boolean {
  return isTMOverrideEligibleType(line.type) && line.isCustom !== true;
}

/**
 * Did an operator type this line, rather than pick it from the pricebook?
 *
 * Lockstep with `isManualPricingLine` in the operations pricing engine, which
 * is the code that actually bills. If the two disagree, the editor shows one
 * rule and the customer is charged by another.
 *
 * `source` is spelled in two vocabularies and both have to be read: `MANUAL`
 * on a sales document (against `CATALOG`), `custom` on a job (against
 * `pricebook`). A job's custom line also stores the sentinel `code: 'CUSTOM'`,
 * so the blank-code tell — the only one a line written before `source` has —
 * never reaches it.
 */
export function isManualLine(line: {
  source?: string | null;
  isCustom?: boolean;
  code?: string | null;
}): boolean {
  const source = String(line.source ?? '')
    .trim()
    .toUpperCase();
  if (source === 'MANUAL' || source === 'CUSTOM') return true;
  if (line.isCustom === true) return true;
  return !String(line.code ?? '').trim();
}

/** A stored line, as far as reading its price back is concerned. */
export interface StoredLinePricing {
  type?: string | null;
  isCustom?: boolean;
  priceStrategy?: string | null;
  basePrice?: number | null;
  laborRate?: number | null;
  estimatedHours?: number | null;
  costBasis?: number | null;
  markupPercent?: number | null;
  quantity?: number | null;
}

/**
 * The strategy and unit price a stored line is read back into the editor with.
 *
 * A pass-through for everything the catalog priced. For a hand-typed line saved
 * as DYNAMIC — which the editor used to allow and no longer does, see
 * {@link allowsDynamicPricing} — it FOLDS the derived amount into the unit
 * price rather than dropping to a bare `basePrice` of 0. The derivation and the
 * flat price then agree to the cent (`basePrice x quantity` is the subtotal the
 * document already had), so reopening such an estimate cannot silently move its
 * total, and the next save stores it as the flat line it now is.
 */
export function flattenCustomLinePricing(line: StoredLinePricing): {
  priceStrategy: string;
  basePrice: number;
} {
  const priceStrategy = String(line.priceStrategy ?? 'FLAT');
  const basePrice = Number(line.basePrice ?? 0);
  if (allowsDynamicPricing(line) || priceStrategy.trim().toUpperCase() !== 'DYNAMIC') {
    return { priceStrategy, basePrice };
  }

  const quantity = Number(line.quantity ?? 1);
  // Asked at `quantity: 1`, because a UNIT price is what this returns. The
  // formula is per unit — hours are the time ONE delivery takes — so the caller
  // multiplying the quantity back in is the whole of the scaling, and dividing a
  // whole-line total by it here would be undoing a multiplication that no longer
  // happens (NOT-630).
  const unitPrice = resolveTMLineSubtotal({
    quantity: 1,
    basePrice,
    laborRate: line.laborRate,
    costBasis: line.costBasis,
    markupPercent: line.markupPercent,
    estimatedHours: line.estimatedHours ?? undefined,
  });

  // A zero quantity has no unit price to state, and the line bills nothing
  // either way — keep what was stored rather than inventing one for it.
  return {
    priceStrategy: 'FLAT',
    basePrice: quantity > 0 ? unitPrice : basePrice,
  };
}
